import { toast } from 'react-toastify';

// Base URL for API calls - read from environment variables or use default
const PYTHON_API_BASE_URL = process.env.REACT_APP_PYTHON_BACKEND_URL;
const NODE_SERVER_URL = process.env.REACT_APP_NODE_SERVER_URL;

/**
 * Makes a request to create optimal clusters for delivery
 * @param {string} deliveryDate - The delivery date in YYYY-MM-DD format
 * @param {number} numberOfDrivers - The number of drivers available
 * @returns {Promise} - A promise that resolves to the clusters data
 */
export const createDeliveryClusters = async (deliveryDate, selectedDrivers = []) => {
  try {
    // Create a timestamp-based iteration ID
    const iterationId = `${Date.now()}`;
    
    // Convert selectedDrivers array to comma-separated string
    const driversParam = selectedDrivers.join(',');
    
    const response = await fetch(`${PYTHON_API_BASE_URL}/api/makebestclusters?delivery_date=${deliveryDate}&drivers=${driversParam}&iteration_id=${iterationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create clusters: ${errorText}`);
    }

    // Try to parse the response as JSON
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating delivery clusters:', error);
    toast.error('Failed to create delivery clusters. Please try again.');
    throw error;
  }
};

export const transferShipmentToDifferentDriver = async (shipment, delivery_date) => {
  try {
    console.log('URL TO TRANSFER', `${PYTHON_API_BASE_URL}/api/transferShipment?delivery_date=${delivery_date}&from_driver=${shipment.driverNumber}&to_driver=${shipment.newDriverNumber}&shipment_number=${shipment['Delivery Name']}`);
    const response = await fetch(`${PYTHON_API_BASE_URL}/api/transferShipment?delivery_date=${delivery_date}&from_driver=${shipment.driverNumber}&to_driver=${shipment.newDriverNumber}&shipment_number=${shipment['Delivery Name']}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to transfer shipment: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error transferring shipment to different driver:', error);
    toast.error('Failed to transfer shipment to different driver. Please try again.');
    throw error;
  }
}

/**
 * Fetches shipments for a specific delivery date from the new endpoint
 * @param {string} date - The delivery date in YYYY-MM-DD format
 * @returns {Promise} - A promise that resolves to the shipments data
 */
export const fetchDeliveryDateShipments = async (date) => {
  try {
    // Format the date to YYYY-MM-DD if it's not already
    const formattedDate = date instanceof Date
      ? date.toISOString().split('T')[0]
      : date;
      

    const url = `${NODE_SERVER_URL}/api/delivery_date_shipments/${formattedDate}`;

    console.log('Fetching shipments data from endpoint:', {
      date: formattedDate,
      url
    });

    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      
      // If 404, the data likely doesn't exist yet
      if (response.status === 404) {
        throw new Error(`No shipment data available for ${formattedDate}`);
      }
      
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.details || `Failed to fetch shipments data (Status: ${response.status})`);
      } catch (e) {
        throw new Error(`Failed to fetch shipments data: ${errorText || response.statusText}`);
      }
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to fetch shipments data');
    }

    // Transform the data to match the format expected by the app
    const shipments = responseData.data.map(shipment => {
      if (shipment.delivery_partner === 'np_pickup' || shipment.delivery_partner === 'gls_pickup') {
        return {
          ...shipment,
          latitude: shipment?.sender?.address?.latitude,
          longitude: shipment?.sender?.address?.longitude,
          address: shipment?.sender?.address?.string,
        };
      }
      return {
        ...shipment,
        latitude: shipment?.recipient?.address?.latitude,
        longitude: shipment?.recipient?.address?.longitude,
        address: shipment?.recipient?.address?.string,
      }
    });

    
    console.log(`Fetched ${shipments.length} shipments for date ${formattedDate}`);
    
    return shipments;
  } catch (error) {
    console.error('Error fetching shipments:', error);
    throw error;
  }
};

export const updateShipmentDeliveryType = async (shipmentNumber, deliveryType) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/shipment/change_delivery_type`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      shipment_number: shipmentNumber,
      custom_delivery_type: deliveryType
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update shipment delivery type: ${errorText}`);
  }

  return await response.json();
}

export const deleteShipment = async (shipmentNumber) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/shipment/delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      shipment_number: shipmentNumber
    })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete shipment: ${errorText}`);
  }

  return await response.json();
}

export const addShipments = async (deliveryDate, shipmentNumbers) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/shipment/add`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      delivery_date: deliveryDate,
      shipment_numbers: shipmentNumbers
    })  
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to add shipments: ${errorText}`);
  } 

  return await response.json();
}

/**
 * Creates a custom shipment
 * @param {Object} shipmentData - The shipment data
 * @param {string} shipmentData.name - The shipment number/name
 * @param {string} shipmentData.address - The delivery address
 * @param {number} shipmentData.latitude - The latitude of the delivery location
 * @param {number} shipmentData.longitude - The longitude of the delivery location
 * @param {string} [shipmentData.customerName] - The customer name (optional)
 * @param {string} [shipmentData.phone] - The customer phone number (optional)
 * @param {string} shipmentData.delivery_date - The delivery date in YYYY-MM-DD format
 * @returns {Promise<Object>} The created shipment data
 */
export const createCustomShipment = async (shipmentData) => {
  console.log('shipmentData', shipmentData);
  const response = await fetch(`${NODE_SERVER_URL}/api/shipment/create-custom`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(shipmentData)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to create custom shipment: ${errorText}`);
  }

  return await response.json();
}
 /* Fetches all drivers from the API
 * @returns {Promise} - A promise that resolves to the drivers data
 */
export const fetchDrivers = async () => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/drivers`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch drivers: ${errorText}`);
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to fetch drivers data');
    }

    return responseData.data;
  } catch (error) {
    console.error('Error fetching drivers:', error);
    throw error;
  }
};

/**
 * Adds a new driver
 * @param {Object} driverData - The driver data to add (name, home_address, phone, email)
 * @returns {Promise} - A promise that resolves to the added driver data
 */
export const addDriver = async (driverData) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/drivers/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(driverData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to add driver: ${errorText}`);
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to add driver');
    }

    return responseData.data;
  } catch (error) {
    console.error('Error adding driver:', error);
    throw error;
  }
};

/**
 * Updates an existing driver
 * @param {number} id - The ID of the driver to update
 * @param {Object} driverData - The driver data to update (name, home_address)
 * @returns {Promise} - A promise that resolves to the updated driver data
 */
export const updateDriver = async (id, driverData) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/drivers/edit/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(driverData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update driver: ${errorText}`);
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to update driver');
    }

    return responseData.data;
  } catch (error) {
    console.error('Error updating driver:', error);
    throw error;
  }
};

/**
 * Deletes a driver
 * @param {number} id - The ID of the driver to delete
 * @returns {Promise} - A promise that resolves when the driver is deleted
 */
export const deleteDriver = async (id) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/drivers/delete/${id}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to delete driver: ${errorText}`);
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to delete driver');
    }

    return responseData.data;
  } catch (error) {
    console.error('Error deleting driver:', error);
    throw error;
  }
};

/**
 * Fetches shipment details for a specific iteration_id from best_clusters table
 * @param {string} iterationId - The iteration_id to fetch clusters for
 * @returns {Promise} - A promise that resolves to the clusters data
 */
export const fetchClustersByIterationId = async (iterationId) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/shipment/clusters/${iterationId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Server error response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      
      // If 404, the data likely doesn't exist yet
      if (response.status === 404) {
        throw new Error(`No clusters found for iteration ID ${iterationId}`);
      }
      
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.details || `Failed to fetch clusters data (Status: ${response.status})`);
      } catch (e) {
        throw new Error(`Failed to fetch clusters data: ${errorText || response.statusText}`);
      }
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to fetch clusters data');
    }

    console.log(`Fetched ${responseData.count} shipments for iteration ID ${iterationId}`);
    
    return responseData.data;
  } catch (error) {
    console.error('Error fetching clusters by iteration ID:', error);
    throw error;
  }
};

/**
 * Updates the driver_id for a specific shipment in the best_clusters table
 * @param {string} shipmentName - The shipment name to update
 * @param {number} driverId - The new driver ID to assign
 * @param {string} iterationId - The iteration ID for the clustering
 * @returns {Promise} - A promise that resolves to the updated data
 */
export const updateShipmentDriver = async (shipmentName, driverId, iterationId) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/shipment/clusters/update-driver`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        shipment_name: shipmentName,
        driver_id: driverId,
        iteration_id: iterationId
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update driver assignment');
    }

    const responseData = await response.json();
    return responseData.data;
  } catch (error) {
    console.error('Error updating driver assignment:', error);
    throw error;
  }
};

export const requestOptimalRoutes = async ({
  deliveryDate,
  clustersIterationId,
  iterationId,
}) => {
  const response = await fetch(`${PYTHON_API_BASE_URL}/api/makebestroute?delivery_date=${deliveryDate}&best_clusters_iteration_id=${clustersIterationId}&iteration_id=${iterationId}`, {
    method: 'POST',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to request optimal routes: ${errorText}`);
  }

  return await response.json();
}

export const fetchOptimalRoutes = async (deliveryDate, iterationId) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/get_optimal_routes?delivery_date=${deliveryDate}&iteration_id=${iterationId}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch optimal routes: ${errorText}`);
  }

  return await response.json();
}

/**
 * Updates the sequence values for a set of shipments in a route
 * @param {string} deliveryDate - The delivery date in YYYY-MM-DD format
 * @param {string} iterationId - The iteration ID
 * @param {Array} shipmentSequences - Array of objects with shipment_name and sequence
 * @returns {Promise<Object>} The response from the server
 */
export const updateShipmentSequences = async (deliveryDate, iterationId, shipmentSequences) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/routes/update_sequences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        delivery_date: deliveryDate,
        iteration_id: iterationId,
        shipmentSequences
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update shipment sequences: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating shipment sequences:', error);
    throw error;
  }
}

/**
 * Saves a delivery plan to the database
 * @param {string} deliveryDate - The delivery date in YYYY-MM-DD format
 * @param {string} iterationId - The iteration ID
 * @returns {Promise<Object>} The response from the server
 */
export const saveDeliveryPlan = async (deliveryDate, iterationId) => {
  try {
    const response = await fetch(`${NODE_SERVER_URL}/api/deliveryPlans/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        delivery_date: deliveryDate,
        iteration_id: iterationId
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to save delivery plan: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving delivery plan:', error);
    throw error;
  }
}

/**
 * Fetches shipments data for the scanning interface
 * @param {string|Date} date - The delivery date in YYYY-MM-DD format or Date object
 * @returns {Promise<Object>} Contains shipmentsByDriver and deliveryToDriverMap
 */
export const fetchShipmentsForScanning = async (date) => {
  try {
    // Format the date to YYYY-MM-DD if it's not already
    const formattedDate = date instanceof Date
      ? date.toISOString().split('T')[0]
      : date;

    const url = `${NODE_SERVER_URL}/api/deliveryPlans/shipments?delivery_date=${formattedDate}`;
    
    console.log('Fetching shipments for scanning:', {
      date: formattedDate,
      url
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      // If 404, no saved plan exists for this date
      if (response.status === 404) {
        throw new Error(`No saved delivery plan available for ${formattedDate}`);
      }
      
      try {
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || `Failed to fetch shipments (Status: ${response.status})`);
      } catch (e) {
        throw new Error(`Failed to fetch shipments: ${errorText || response.statusText}`);
      }
    }

    const responseData = await response.json();
    
    if (!responseData.success) {
      throw new Error(responseData.error || 'Failed to fetch shipments data');
    }

    console.log(`Fetched scanning data for date ${formattedDate}:`, responseData.data);
    
    return responseData.data;
  } catch (error) {
    console.error('Error fetching shipments for scanning:', error);
    throw error;
  }
};

export const fetchShipmentsSelfPickup = async (date) => {
  const formattedDate = date instanceof Date
    ? date.toISOString().split('T')[0]
    : date;

  const url = `${NODE_SERVER_URL}/api/shipment/self-pickup/${formattedDate} `;

  const response = await fetch(url, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch shipments for self pickup: ${errorText}`);
  }

  return await response.json();
}

export const addShipmentsToRoute = async (data, deliveryDate, iterationId) => {

  const formattedData = data.map(item => ({
    shipment_name: item.id,
    driver_id: item.driver,
    sequence: item.sequence,
    delivery_date: deliveryDate,
    iteration_id: iterationId
  }))
  
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/add_shipment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify(formattedData)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to add shipments to route: ${errorText}`);
  }

  return await response.json();
}

export const updateTelegramBot = async (deliveryDate, iterationId) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/update_telegram_bot`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ delivery_date: deliveryDate, iteration_id: iterationId })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to update Telegram bot: ${errorText}`);
  }

  return await response.json();
}

export const fetchExistingRoutePlans = async () => {
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/get_existing_route_plans`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch existing route plans: ${errorText}`);
  }

  return await response.json();
}

/**
 * Removes one or more shipments from a route (batch operation)
 * @param {string|string[]} shipmentNames - Single shipment name or array of shipment names to remove
 * @param {string} iterationId - The iteration ID
 * @param {number} driverId - The driver ID
 * @returns {Promise<Object>} Response with removed_shipments array and removed_count
 */
export const removeShipmentFromRoute = async (shipmentNames, iterationId, driverId) => {
  // Normalize to array for batch API
  const shipmentNamesArray = Array.isArray(shipmentNames) ? shipmentNames : [shipmentNames];
  
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/remove_shipment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ 
      shipment_names: shipmentNamesArray, 
      iteration_id: iterationId, 
      driver_id: driverId 
    })
  });

  return await response.json();
}

export const resetShipmentStatus = async (shipmentName, iterationId, driverId) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/reset_shipment_status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ shipment_name: shipmentName, iteration_id: iterationId, driver_id: driverId })
  });

  return await response.json();
}

export const addCommentToShipment = async (iterationId, shipmentName, shipmentComment) => {
  const response = await fetch(`${NODE_SERVER_URL}/api/routes/add_comment_to_shipment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({ iteration_id: iterationId, shipment_name: shipmentName, shipment_comment: shipmentComment })
  });

  return await response.json();
}